import React from 'react'
import {
    BeforeContactAddress,
    ContactInformation,
    DestinationContactAddress,
    NewMemberInfo,
    PaymentContactAddress,
    ProxyContactAddress,
    SharerUser
} from '../repository/NatGetOwnershipTransfer'
import { Box, TableRow } from '@mui/material';
import {FontWeights} from '../../../../styledComponents/Constants'
import {
    SecondaryTableCell,
    SecondaryTableRowName,
    TableRowName,
    TableRowNameTop
} from '../../../../styledComponents/adminPage/Table'
import {getFormattedAddress, toFullWidthNumber} from '../../../common/util/Natutil'
import moment from 'moment'
import {
    DESTINATION_EXISTENCE,
    DO_OR_NOT,
    PAYMENT_EXISTENCE,
    RESIDENT_PLACE,
    ROLE_FOR_ADMINISTRATOR
} from '../../../../CodeMaster'
import {Nullable} from '../../../../app/TypeHelpers'
import {SessionStorageHelper} from '../../../../SessionStorageHelper'

const KEY_LOGIN_USER_ROLE_ID: string = 'loginUserRoleId'

interface Props {
    newMemberInfo: Nullable<NewMemberInfo>
}

export const NatOwnershipTransferApplyDetailByNewMember: React.FC<Props> = props => {
    const roleId = SessionStorageHelper.getStringItemOrNull(KEY_LOGIN_USER_ROLE_ID)
    const lmFlag = roleId === ROLE_FOR_ADMINISTRATOR.LIFE_MANAGER.value

    if (!props.newMemberInfo) {
        return (
            <TableRow>
                <TableRowName>
                    <Box fontWeight={FontWeights.Bold}>新組合員</Box>
                </TableRowName>
                <SecondaryTableCell colSpan={4} end>
                    入力されていません。
                </SecondaryTableCell>
            </TableRow>
        )
    }

    const {
        newMemberFamilyName,
        newMemberFirstName,
        newMemberFamilyNameKana,
        newMemberFirstNameKana,
        newMemberMailAddress,
        sharerUserInfo,
        residentPlace,
        moveInDate,
        contactInformation,
        beforeContactAddress,
        proxyContactAddress,
        rentExistence,
        paymentExistence,
        paymentContactAddress,
        destinationExistence,
        destinationContactAddress,
    } = props.newMemberInfo

    const newMemberRowSpan = newMemberInfoRowCalculation(
        sharerUserInfo,
        residentPlace,
        moveInDate,
        beforeContactAddress,
        rentExistence,
        paymentExistence,
        destinationExistence
    )

    return (
        <>
            <TableRow>
                <TableRowNameTop rowSpan={newMemberRowSpan}>
                    <Box fontWeight={FontWeights.Bold}>新組合員</Box>
                </TableRowNameTop>
                <SecondaryTableRowName rowSpan={lmFlag ? 1 : 2}>
                    新組合員情報
                </SecondaryTableRowName>
                <SecondaryTableRowName>
                    氏名
                </SecondaryTableRowName>
                <SecondaryTableCell colSpan={2}>
                    {newMemberFamilyName} {newMemberFirstName}（{newMemberFamilyNameKana} {newMemberFirstNameKana}）
                </SecondaryTableCell>
            </TableRow>
            {!lmFlag &&
                <TableRow>
                    <SecondaryTableRowName>
                        メールアドレス
                    </SecondaryTableRowName>
                    <SecondaryTableCell colSpan={3}>
                        {newMemberMailAddress}
                    </SecondaryTableCell>
                </TableRow>
            }
            {renderSharerUserInfo(sharerUserInfo)}
            <TableRow>
                <SecondaryTableRowName>
                    居住先
                </SecondaryTableRowName>
                <SecondaryTableCell colSpan={3}>
                    この部屋に{RESIDENT_PLACE.codeToEnumConstants(residentPlace).label}
                </SecondaryTableCell>
            </TableRow>
            {residentPlace === RESIDENT_PLACE.RESIDING.value &&
                renderMoveInInfo(moveInDate, beforeContactAddress)
            }
            {renderContactAddressInfo(
                residentPlace,
                contactInformation,
                proxyContactAddress
            )}
            {(residentPlace === RESIDENT_PLACE.DOMESTIC.value ||
                    residentPlace === RESIDENT_PLACE.FOREIGN.value) &&
                <TableRow>
                    <SecondaryTableRowName>
                        賃貸
                    </SecondaryTableRowName>
                    <SecondaryTableCell colSpan={3}>
                        {rentExistence === DO_OR_NOT.DO.value ?
                            `賃貸する` : '賃貸しない'
                        }
                    </SecondaryTableCell>
                </TableRow>
            }
            {renderPayment(
                paymentExistence,
                paymentContactAddress)}
            {renderDestination(
                destinationExistence,
                destinationContactAddress)}
        </>
    )
}

const renderSharerUserInfo = (sharerUserInfo: SharerUser[]) => {
    return (
        <>
            {sharerUserInfo.map((sharer, index) => {
                const {seq, sharerFamilyName, sharerFirstName, sharerFamilyNameKana, sharerFirstNameKana} = sharer
                return (
                    <TableRow key={index}>
                        {index === 0 &&
                            <SecondaryTableRowName rowSpan={sharerUserInfo.length}>
                                共有者情報
                            </SecondaryTableRowName>
                        }
                        <SecondaryTableRowName>
                            共有者{toFullWidthNumber(seq)}
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {sharerFamilyName} {sharerFirstName}（{sharerFamilyNameKana} {sharerFirstNameKana}）
                        </SecondaryTableCell>
                    </TableRow>
                )
            })}
        </>
    )
}

const renderMoveInInfo = (
    moveInDate: string,
    beforeContactAddress: BeforeContactAddress
) => {
    return (
        <>
            <TableRow>
                <SecondaryTableRowName>
                    入居予定日
                </SecondaryTableRowName>
                <SecondaryTableCell colSpan={3}>
                    {moment(moveInDate).format('YYYY年M月D日')}
                </SecondaryTableCell>
            </TableRow>
            {beforeContactAddress.beforeContactPhoneNumber &&
                <>
                    <TableRow>
                        <SecondaryTableRowName rowSpan={2}>
                            入居までの連絡先
                        </SecondaryTableRowName>
                        <SecondaryTableRowName>
                            電話番号
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {beforeContactAddress.beforeContactPhoneNumber}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName>
                            住所
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {getFormattedAddress(
                                beforeContactAddress.beforeContactZipCode,
                                beforeContactAddress.beforeContactPrefecture,
                                beforeContactAddress.beforeContactAddress2,
                                beforeContactAddress.beforeContactAddress3,
                                beforeContactAddress.beforeContactBuildingRoomNumber
                            )}
                        </SecondaryTableCell>
                    </TableRow>
                </>
            }
        </>
    )
}

const renderContactAddressInfo = (
    residentPlace: string,
    contactInformation: ContactInformation,
    proxyContactAddress: ProxyContactAddress
) => {
    return (
        <>
            <TableRow>
                <SecondaryTableRowName rowSpan={2}>
                    {residentPlace === RESIDENT_PLACE.RESIDING.value ? `入居後の連絡先` : '連絡先'}
                </SecondaryTableRowName>
                <SecondaryTableRowName>
                    電話番号
                </SecondaryTableRowName>
                <SecondaryTableCell colSpan={2}>
                    {contactInformation.contactPhoneNumber}
                </SecondaryTableCell>
            </TableRow>
            <TableRow>
                <SecondaryTableRowName>
                    住所
                </SecondaryTableRowName>
                <SecondaryTableCell colSpan={2}>
                    {
                        residentPlace === RESIDENT_PLACE.RESIDING.value ||
                        residentPlace === RESIDENT_PLACE.DOMESTIC.value ?
                            getFormattedAddress(
                                contactInformation.contactZipCode,
                                contactInformation.contactPrefecture,
                                contactInformation.contactAddress2,
                                contactInformation.contactAddress3,
                                contactInformation.contactBuildingRoomNumber
                            ) :
                            contactInformation.contactAddress
                    }
                </SecondaryTableCell>
            </TableRow>
            {
                residentPlace === RESIDENT_PLACE.FOREIGN.value &&
                <>
                    <TableRow>
                        <SecondaryTableRowName rowSpan={4}>
                            国内の代理連絡先
                        </SecondaryTableRowName>
                        <SecondaryTableRowName>
                            組合員との関係性
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {proxyContactAddress.proxyContactRelationship}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName>
                            氏名
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {proxyContactAddress.proxyContactFamilyName} {proxyContactAddress.proxyContactFirstName}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName>
                            電話番号
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {proxyContactAddress.proxyContactPhoneNumber}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName>
                            住所
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {getFormattedAddress(
                                proxyContactAddress.proxyContactZipCode,
                                proxyContactAddress.proxyContactPrefecture,
                                proxyContactAddress.proxyContactAddress2,
                                proxyContactAddress.proxyContactAddress3,
                                proxyContactAddress.proxyContactBuildingRoomNumber
                            )}
                        </SecondaryTableCell>
                    </TableRow>
                </>
            }
        </>
    )
}

const renderPayment = (
    paymentExistence: string,
    paymentContactAddress: PaymentContactAddress
) => {
    const paymentExistenceMaster = PAYMENT_EXISTENCE.codeToEnumConstants(paymentExistence)

    return (
        <>
            <TableRow>
                <SecondaryTableRowName>
                    管理費等の支払情報
                </SecondaryTableRowName>
                <SecondaryTableCell colSpan={3}>
                    {paymentExistenceMaster.label}
                </SecondaryTableCell>
            </TableRow>
            {paymentExistenceMaster === PAYMENT_EXISTENCE.AGENT &&
                <>
                    <TableRow>
                        <SecondaryTableRowName rowSpan={4}>
                            支払者情報
                        </SecondaryTableRowName>
                        <SecondaryTableRowName>
                            組合員との関係性
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {paymentContactAddress.paymentRelationship}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName>
                            氏名または法人名
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {paymentContactAddress.paymentName}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName>
                            電話番号
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {paymentContactAddress.paymentPhoneNumber}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName>
                            住所
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {getFormattedAddress(
                                paymentContactAddress.paymentZipCode,
                                paymentContactAddress.paymentPrefecture,
                                paymentContactAddress.paymentAddress2,
                                paymentContactAddress.paymentAddress3,
                                paymentContactAddress.paymentBuildingRoomNumber
                            )}
                        </SecondaryTableCell>
                    </TableRow>
                </>
            }
        </>
    )
}

const renderDestination = (
    destinationExistence: string,
    destinationContactAddress: DestinationContactAddress
) => {
    const destinationExistenceMaster = DESTINATION_EXISTENCE.codeToEnumConstants(destinationExistence)

    return (
        <>
            <TableRow>
                <SecondaryTableRowName end={destinationExistenceMaster === DESTINATION_EXISTENCE.PRINCIPAL}>
                    請求書の送付先情報
                </SecondaryTableRowName>
                <SecondaryTableCell colSpan={3} end={destinationExistenceMaster === DESTINATION_EXISTENCE.PRINCIPAL}>
                    {destinationExistenceMaster.label}
                </SecondaryTableCell>
            </TableRow>
            {destinationExistenceMaster === DESTINATION_EXISTENCE.AGENT &&
                <>
                    <TableRow>
                        <SecondaryTableRowName rowSpan={3} end>
                            送付先情報
                        </SecondaryTableRowName>
                        <SecondaryTableRowName>
                            氏名または法人名
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {destinationContactAddress.destinationName}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName>
                            電話番号
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2}>
                            {destinationContactAddress.destinationPhoneNumber}
                        </SecondaryTableCell>
                    </TableRow>
                    <TableRow>
                        <SecondaryTableRowName end>
                            住所
                        </SecondaryTableRowName>
                        <SecondaryTableCell colSpan={2} end>
                            {getFormattedAddress(
                                destinationContactAddress.destinationZipCode,
                                destinationContactAddress.destinationPrefecture,
                                destinationContactAddress.destinationAddress2,
                                destinationContactAddress.destinationAddress3,
                                destinationContactAddress.destinationBuildingRoomNumber
                            )}
                        </SecondaryTableCell>
                    </TableRow>
                </>
            }
        </>
    )
}

const newMemberInfoRowCalculation = (
    sharerUserInfo: SharerUser[],
    residentPlace: string,
    moveInDate: string,
    beforeContactAddress: BeforeContactAddress,
    rentExistence: string,
    paymentExistence: string,
    destinationExistence: string
) => {
    const baseRow = 7

    const shereUserRow = sharerUserInfo.length
    const residentPlaceRow = residentPlace === RESIDENT_PLACE.FOREIGN.value ? 4 : 0
    const moveInDateRow = moveInDate ? 1 : 0
    const rentExistenceRow = residentPlace !== RESIDENT_PLACE.RESIDING.value ? 1 : 0
    const beforeContactAddressRow = beforeContactAddress.beforeContactPhoneNumber ? 2 : 0
    const paymentExistenceRow = paymentExistence === PAYMENT_EXISTENCE.AGENT.value ? 4 : 0
    const destinationExistenceRow = destinationExistence === DESTINATION_EXISTENCE.AGENT.value ? 3 : 0

    return baseRow +
        shereUserRow +
        residentPlaceRow +
        moveInDateRow +
        rentExistenceRow +
        beforeContactAddressRow +
        paymentExistenceRow +
        destinationExistenceRow
}
